import { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import Alert from 'react-bootstrap/Alert';
import OrderCard from '../molecules/OrderCard';
import OrderDetailModal from './OrderDetailModal';
import AppSpinner from '../atoms/AppSpinner';
import { IconInbox, IconSearch } from '../atoms/Icon';
import { useToast } from '../contexts/ToastContext';
import { listOrders, updateOrderStatus, COLUMNS, STATUSES } from '../api/ordersApi';
import { getCached, setCached } from '../api/sessionCache';

const ORDERS_CACHE_KEY = 'orders';

function matchesSearch(order, term) {
  if (!term) return true;
  const q = term.trim().toLowerCase();
  return order.customerName.toLowerCase().includes(q) || String(order.number).includes(q);
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 0.02}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

function EmptyState({ isFiltering }) {
  return (
    <div className="text-center py-4" style={{ color: '#b3b3b3' }}>
      {isFiltering ? (
        <>
          <IconSearch size={26} />
          <p className="mt-2 mb-0" style={{ fontSize: 12 }}>Nenhum pedido encontrado.</p>
        </>
      ) : (
        <>
          <IconInbox size={26} />
          <p className="mt-2 mb-0" style={{ fontSize: 12 }}>Nenhum pedido nesta etapa.</p>
        </>
      )}
    </div>
  );
}

// Sem websocket: a tela se atualiza sozinha perguntando ao servidor a cada 15s.
const POLL_INTERVAL = 15000;

function OrdersKanban({ search = '', reloadKey, onStatsChange }) {
  const cachedOrders = getCached(ORDERS_CACHE_KEY);
  const [orders, setOrders] = useState(cachedOrders || []);
  const [loading, setLoading] = useState(!cachedOrders);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState(COLUMNS[0].id);
  const isMobile = useIsMobile(768);
  const toast = useToast();

  // Enquanto um arrasto está sendo confirmado pelo servidor, a resposta do poll
  // ainda traz o status antigo — aplicá-la faria o card pular de volta.
  const pendingMoves = useRef(0);

  const load = useCallback(async () => {
    try {
      const data = await listOrders();
      if (pendingMoves.current === 0) setOrders(data);
      setCached(ORDERS_CACHE_KEY, data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  useEffect(() => {
    const tick = () => { if (!document.hidden) load(); };
    const interval = setInterval(tick, POLL_INTERVAL);
    // Voltando para a aba, atualiza na hora em vez de esperar o próximo ciclo.
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const ordersIn = useCallback(
    (status) => orders.filter((o) => o.status === status),
    [orders],
  );

  useEffect(() => {
    if (!onStatsChange) return;
    const active = orders.filter(
      (o) => o.status === STATUSES.awaitingPayment || o.status === STATUSES.preparing,
    );
    onStatsChange({
      inProgress: active.length,
      doneToday: orders.filter((o) => o.status === STATUSES.ready).length,
      revenue: active.reduce((sum, o) => sum + Number(o.totalAmount), 0),
    });
  }, [orders, onStatsChange]);

  // Otimista: o card muda de coluna na hora e o servidor confirma depois. Numa
  // cozinha, esperar a resposta a cada arrasto trava a operação.
  async function moveOrder(order, toStatus) {
    const previous = orders;
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: toStatus } : o)));
    pendingMoves.current += 1;
    try {
      const updated = await updateOrderStatus(order.id, toStatus);
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    } catch (e) {
      setOrders(previous);
      toast.error(e.message);
    } finally {
      pendingMoves.current -= 1;
    }
  }

  function advanceOrder(order, fromStatus) {
    const index = COLUMNS.findIndex((c) => c.id === fromStatus);
    const next = COLUMNS[index + 1];
    if (next) moveOrder(order, next.id);
  }

  function onDragEnd({ source, destination, draggableId }) {
    if (!destination || source.droppableId === destination.droppableId) return;
    const order = orders.find((o) => o.id === draggableId);
    if (order) moveOrder(order, destination.droppableId);
  }

  async function cancelOrder(order) {
    await moveOrder(order, STATUSES.cancelled);
  }

  const isFiltering = !!search.trim();

  if (loading) return <AppSpinner />;

  function renderCards(col, colIndex) {
    const visibleOrders = ordersIn(col.id).filter((o) => matchesSearch(o, search));
    const nextLabel = COLUMNS[colIndex + 1]?.label;

    return (
      <Droppable droppableId={col.id} isDropDisabled={isFiltering}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              minHeight: 80,
              backgroundColor: snapshot.isDraggingOver ? col.bg : 'transparent',
              borderRadius: 12,
              padding: 10,
              transition: 'background-color 0.2s',
              border: snapshot.isDraggingOver
                ? `1.5px dashed ${col.color}`
                : '1.5px dashed transparent',
            }}
          >
            {visibleOrders.length === 0 && <EmptyState isFiltering={isFiltering} />}
            {visibleOrders.map((order, index) => (
              <OrderCard
                key={order.id}
                order={order}
                index={index}
                color={col.color}
                bg={col.bg}
                dragDisabled={isFiltering}
                nextLabel={nextLabel}
                onDetails={() => setSelected({ order, columnId: col.id })}
                onAdvance={() => advanceOrder(order, col.id)}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mb-3" style={{ borderRadius: 10 }}>
          Não foi possível carregar os pedidos: {error}
        </Alert>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        {isMobile ? (
          <div>
            {/* Aba por etapa — em vez de rolar colunas para os lados, o pedido
                muda de etapa pelo botão "Avançar" do próprio card. */}
            <div className="d-flex gap-2 mb-3">
              {COLUMNS.map((col) => {
                const count = ordersIn(col.id).filter((o) => matchesSearch(o, search)).length;
                const active = activeTab === col.id;
                return (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => setActiveTab(col.id)}
                    className="d-flex align-items-center justify-content-center gap-1"
                    style={{
                      flex: '1 1 0',
                      minWidth: 0,
                      border: 'none',
                      borderRadius: 20,
                      padding: '8px 6px',
                      fontSize: 12.5,
                      fontWeight: 600,
                      backgroundColor: active ? col.color : col.bg,
                      color: active ? '#fff' : col.color,
                      transition: 'background-color 0.15s, color 0.15s',
                    }}
                  >
                    <span className="text-truncate">{col.shortLabel}</span>
                    <span
                      style={{
                        flexShrink: 0,
                        backgroundColor: active ? 'rgba(255,255,255,0.25)' : '#fff',
                        color: active ? '#fff' : col.color,
                        borderRadius: 10,
                        padding: '1px 7px',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {COLUMNS.map((col, colIndex) => (
              <div key={col.id} style={{ display: activeTab === col.id ? 'block' : 'none' }}>
                {renderCards(col, colIndex)}
              </div>
            ))}
          </div>
        ) : (
          <div
            className="d-flex gap-3 align-items-start kanban-scroll"
            style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 12, scrollSnapType: 'x proximity' }}
          >
            {COLUMNS.map((col, colIndex) => (
              <div
                key={col.id}
                className="kanban-column"
                style={{
                  backgroundColor: col.colBg,
                  borderRadius: 14,
                  padding: '14px 12px',
                  scrollSnapAlign: 'start',
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-between mb-3 px-1 pb-2"
                  style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                >
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      backgroundColor: col.color, flexShrink: 0,
                    }} />
                    <span
                      className="fw-semibold text-truncate"
                      style={{ fontSize: 13, color: '#333' }}
                    >
                      {col.label}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: 11, fontWeight: 600,
                      backgroundColor: col.bg,
                      color: col.color,
                      borderRadius: 20,
                      padding: '2px 8px',
                      border: `1px solid ${col.color}22`,
                      flexShrink: 0,
                    }}
                  >
                    {ordersIn(col.id).length}
                  </span>
                </div>

                {renderCards(col, colIndex)}
              </div>
            ))}
          </div>
        )}
      </DragDropContext>

      <OrderDetailModal
        show={!!selected}
        order={selected?.order}
        columnId={selected?.columnId}
        onHide={() => setSelected(null)}
        onCancel={cancelOrder}
      />
    </>
  );
}

export default OrdersKanban;
